<template>
  <ValidationObserver
    ref="observer"
    tag="form">
    <BaseTextareaGroupWithProvider
      :value="form.name"
      label="テンプレート名（試合用・練習用など）"
      name="テンプレート名"
      auto-grow
      :response-errors="responseErrors"
      rules="required|max:40"
      vid="name"
      @input="onInputName" />

    <div class="form-group">
      <LabelText>このテンプレートを表示するグループ</LabelText>
      <NoteTemplateDepartmentSelect
        :is-all-departments.sync="form.isAllDepartments"
        :departments.sync="form.departments"
        :department-options="departmentOptions"
        :disabled="departmentLocked" />
      <BaseErrorMessage :errors="departmentErrors" />
    </div>

    <BaseTextFieldGroupWithProvider
      v-model="form.sortOrder"
      label="テンプレート並び順"
      name="テンプレート並び順"
      col="4"
      type="number"
      :response-errors="responseErrors"
      rules="required"
      vid="sortOrder" />

    <div class="form-group">
      <LabelText label-for="note-template-color">
        色分け
      </LabelText>
      <ValidationProvider
        v-slot="{ errors }"
        name="色分け"
        rules="required"
        vid="color">
        <div>
          <input
            id="note-template-color"
            v-model="form.color"
            type="color"
            class="note-template-form__color"
            data-testid="note-template-color-input">
        </div>
        <BaseErrorMessage :errors="[...errors, ...colorResponseErrors]" />
      </ValidationProvider>
    </div>

    <BaseTextFieldGroupWithProvider
      v-model="form.activityName"
      label="ノート活動名（活動名を統一したい場合は入力）"
      name="ノート活動名"
      placeholder="（例）公式戦 vs"
      rules="max:40"
      :response-errors="responseErrors"
      vid="activityName" />

    <BaseTextareaGroupWithProvider
      v-model="form.detail"
      label="ノート活動記録"
      name="ノート活動記録"
      placeholder="（例）【今日の試合でチャレンジしたことは？】"
      :rows="9"
      rules="required"
      :response-errors="responseErrors"
      vid="detail" />

    <div class="pt-4">
      <ButtonsWrapper>
        <template #leftTop>
          <BaseButton
            color="cancel"
            @click="$router.go(-1)">
            キャンセル
          </BaseButton>
        </template>
        <template #rightTop>
          <BaseButton
            data-testid="note-template-submit-button"
            @click="submit">
            保存
          </BaseButton>
        </template>
      </ButtonsWrapper>
    </div>
  </ValidationObserver>
</template>

<script>
import BaseTextFieldGroupWithProvider from '@/components/parts/BaseTextFieldGroupWithProvider.vue'
import BaseTextareaGroupWithProvider from '@/components/parts/BaseTextareaGroupWithProvider.vue'
import BaseButton from '@/components/parts/BaseButton.vue'
import BaseErrorMessage from '@/components/parts/BaseErrorMessage.vue'
import ButtonsWrapper from '@/components/parts/ButtonsWrapper.vue'
import LabelText from '@/components/parts/LabelText.vue'
import NoteTemplateDepartmentSelect from '@/components/view/note_templates/NoteTemplateDepartmentSelect.vue'

const DEFAULT_COLOR = '#ff6600'
const NO_DEPARTMENT_ERROR = '「すべてのグループ」を有効にするか、表示するグループを1つ以上指定してください。'

export default {
  name: 'NoteTemplateForm',
  components: {
    BaseTextFieldGroupWithProvider,
    BaseTextareaGroupWithProvider,
    BaseButton,
    BaseErrorMessage,
    ButtonsWrapper,
    LabelText,
    NoteTemplateDepartmentSelect
  },
  props: {
    noteTemplate: {
      type: Object,
      default: null
    },
    departmentOptions: {
      type: Array,
      default: () => []
    },
    responseErrors: {
      type: Object,
      default: null
    },
    departmentLocked: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        name: '',
        color: DEFAULT_COLOR,
        sortOrder: 1,
        // 作成時は表示するグループを絞るのが通常のため「個別グループ」で始める
        isAllDepartments: false,
        activityName: '',
        detail: '',
        departments: []
      },
      showDepartmentError: false
    }
  },
  computed: {
    colorResponseErrors() {
      return this.responseErrors?.color || []
    },
    departmentErrors() {
      const errors = [...this.responseErrors?.departments || []]
      if (this.showDepartmentError && !this.isDepartmentValid) {
        errors.unshift(NO_DEPARTMENT_ERROR)
      }
      return errors
    },
    isDepartmentValid() {
      return this.form.isAllDepartments || this.form.departments.length > 0
    }
  },
  created() {
    if (!this.noteTemplate) return
    this.form = {
      name: this.noteTemplate.name ?? '',
      color: this.noteTemplate.color || DEFAULT_COLOR,
      sortOrder: this.noteTemplate.sortOrder ?? 1,
      isAllDepartments: !!this.noteTemplate.isAllDepartments,
      activityName: this.noteTemplate.activityName ?? '',
      detail: this.noteTemplate.detail ?? '',
      departments: (this.noteTemplate.departments || []).map(department => department.id)
    }
  },
  methods: {
    onInputName(value) {
      // textarea なので改行が入りうるが、テンプレート名に改行は意味が無く
      // 一覧では1行として表示されるため空白にまとめる
      this.form.name = String(value ?? '').replace(/\r?\n/g, ' ')
    },
    async submit() {
      this.showDepartmentError = true
      const isValid = await this.$refs.observer.validate()
      if (!isValid || !this.isDepartmentValid) {
        this.$store.commit('notify/set', { message: '入力内容に不備があります。ご確認の上再度実行してください。', type: 'danger' })
        return
      }

      const body = {
        name: this.form.name,
        color: this.form.color,
        sortOrder: Number(this.form.sortOrder),
        activityName: this.form.activityName,
        detail: this.form.detail
      }
      // ロック中はグループ関連のキーごと送らない。
      // 未指定なら API 側で既存のグループ設定が維持されるので、
      // 選択肢に出ていない権限外グループを復元して送り返す必要がない
      if (!this.departmentLocked) {
        body.isAllDepartments = this.form.isAllDepartments
        // 「すべてのグループ」のときは個別グループを送らない
        body.departments = this.form.isAllDepartments ? [] : this.form.departments
      }
      this.$emit('submit', body)
    }
  }
}
</script>

<style lang="scss" scoped>
.note-template-form__color {
  width: 72px;
  height: 32px;
  padding: 0;
  border: 1px solid $grey11;
  border-radius: 4px;
  background-color: $white;
}
</style>
